/**
 * The scheduler run panel's Stats field — the blob a job writes, made readable.
 *
 * Pure on purpose (review F-3). Its regression is "a uuid appears on an operator's
 * screen", and that was only ever proved by a throwaway browser harness inside a
 * worktree: deleting HIDDEN_STAT_KEYS left every check in `pnpm test` green. It now
 * lives beside `cdnHeadline.ts` as a string-in/string-out function pinned by
 * `apps/web/scripts/check-scheduler-stats.mjs`.
 *
 * Deliberately IMPORT-FREE. The check script runs under plain node (apps/web has no
 * test runner), and node resolves neither vite's extensionless imports nor
 * `lib/agentCommands`'s `@apollo/client` graph — so the three-line indent below is
 * kept local rather than reaching for that module's `prettyJson`.
 */

/** `{"a":1}` → indented, one key per line. Non-JSON is returned untouched. */
function indent(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

/**
 * Internal bookkeeping the panel never shows: `runNowJobId` is the dedup id the api
 * stamps on a run-now row (jobs.service.ts recordRunNowFailure), not a stat. A failed
 * run-now row carries ONLY that key, so stripping it collapses the blob to `{}` and
 * the empty guard below removes the whole Stats block — leaving the error line alone.
 */
export const HIDDEN_STAT_KEYS = ['runNowJobId'];

/**
 * `''` means "render no Stats block at all". Everything else is rendered verbatim:
 * valid JSON objects indented (cdn-cost writes 11 keys, and the degradation markers
 * `objects_truncated_files` / `dropped_object_keys` are the last ones a reader needs
 * to see), non-JSON passed through raw rather than swallowed.
 */
export function statsText(s?: string): string {
  if (!s) return '';
  let text = s;
  try {
    const parsed: unknown = JSON.parse(s);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const o = parsed as Record<string, unknown>;
      for (const k of HIDDEN_STAT_KEYS) delete o[k];
      text = JSON.stringify(o);
    }
  } catch {
    // not JSON — fall through and render the raw string
  }
  const t = indent(text).trim();
  return t === '{}' ? '' : t;
}
